import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { finalize, map, tap, catchError } from 'rxjs/operators';
import { Constants } from './constants';
import { IUserActive, IUserOrganization } from './user-active.models';
import { IOrganization } from '../view-routes/inside/admin/point-groups/point-group.models';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../view-shared/dialogs/toast.service';
import { DialogService } from '../view-shared/dialogs/dialog.service';

@Injectable({ providedIn: 'root' })
export class UserActiveService {
	private currentUserSubject: BehaviorSubject<IUserActive>;
	currentUser: Observable<IUserActive>;

	constructor(
		private http: HttpClient,
		private translate: TranslateService,
		private dialogService: DialogService,
		private toastService: ToastService
	) {
		this.currentUserSubject = new BehaviorSubject<IUserActive>(
			JSON.parse(localStorage.getItem('currentUser'))
		);
		this.currentUser = this.currentUserSubject.asObservable();
	}

	get currentUserValue(): IUserActive {
		return this.currentUserSubject.value;
	}

	set currentUserValue(next: IUserActive) {
		this.currentUserSubject.next(next);
	}

	// - - -

	authUser(Email: string, Password: string): Observable<boolean> {
		const zfoo = null;
		return this.http
			.post<any>(`${Constants.API}/Account/Login`, { Email, Password })
			.pipe(
				tap(data => {
					this.parseResponse(data, Email);
				}),
				map(response => response != null)
			);
	}

	authPublic(idTemplate: number): Observable<boolean> {
		return this.http
			.post<any>(`${Constants.API}/Account/LoginPublic`, {
				idTemplate: idTemplate.toString()
			})
			.pipe(
				tap(data => {
					this.parseResponse(data);
				}),
				map(response => response != null)
			);
	}

	parseResponse(data: any, email?: string): void {
		const response = JSON.parse(atob(data.Token.split('.')[1]));
		const user: IUserActive = {
			IdUser:
				response[
					'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
				],
			Name:
				response[
					'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
				],
			Email: email ? email : null,
			Roles:
				response[
					'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
				],
			ImageURL: '',
			Organization: response.Organization
				? ({
						Id: response.Organization,
						Name: response.Organization,
						Description: response.Organisation
				  } as IOrganization)
				: null,
			CanEdit: false,
			Token: data.Token,
			RefreshToken: data.RefreshToken
		};

		this.currentUserSubject.next(user);
		if (email) {
			localStorage.setItem('currentUser', JSON.stringify(user));
		}
	}

	getAuthToken(): string {
		const currentUser = this.currentUserValue;
		if (currentUser != null) {
			return currentUser.Token;
		}
		return '';
	}

	forceRefreshToken(): void {
		this.refreshToken().subscribe();
	}

	refreshToken(): Observable<string> {
		const currentUser: IUserActive = this.currentUserValue;
		return this.http
			.post<any>(`${Constants.API}/Account/Refresh`, {
				Token: currentUser.Token,
				RefreshToken: currentUser.RefreshToken
			})
			.pipe(
				map(data => {
					if (data.Token) {
						this.parseResponse(data, currentUser.Email);
					}
					return this.currentUserValue.Token;
				})
			);
	}

	getAuthUser(): Observable<{ organizations: IUserOrganization[] }> {
		return this.http.get<{ organizations: IUserOrganization[] }>(
			`${Constants.API}/api/lens/user`
		);
	}

	switchOrganization(organizationId: string): Observable<any> {
		return this.http
			.post<any>(`${Constants.API}/api/lens/user/switch-organization`, {
				organizationId
			})
			.pipe(
				tap(data => {
					this.parseResponse(
						{ Token: data?.token },
						this.currentUserValue.Email
					);
				}),
				catchError(err => {
					this.toastService.showToast(
						this.translate.instant(
							'PAGES.ORGANIZATION.SWITCH_FAILED'
						),
						'warning'
					);
					return throwError(err);
				})
			);
	}

	deAuthUser() {
		// remove user from local storage to log user out
		localStorage.removeItem('currentUser');
		this.currentUserSubject.next(null);
	}

	forgotPass(Email: string): Observable<boolean> {
		return this.http
			.post<any>(`${Constants.API}/Profile/SendResetPasswordEmail`, {
				Email
			})
			.pipe(map(response => response != null));
	}

	changePass(OldPassword: string, Password: string): Observable<boolean> {
		const urlRef: string = `${Constants.API}/Profile/UpdateOwnPassword`;
		this.dialogService.showSpinner(
			urlRef,
			this.translate.instant('ROOT.NEW_PASSWORD.CHANGE_PASS')
		);

		return this.http
			.post<any>(urlRef, {
				OldPassword,
				Password,
				ConfirmPassword: Password
			})
			.pipe(
				finalize(() => this.dialogService.closeSpinner(urlRef)),
				catchError(err => {
					this.toastService.showToast(
						this.translate.instant(
							'ROOT.NEW_PASSWORD.CHANGE_PASS_FAILED'
						),
						'warning'
					);
					return throwError(err);
				}),
				map(response => {
					this.toastService.showToast(
						this.translate.instant(
							'ROOT.NEW_PASSWORD.CHANGE_PASS_SUCCESS'
						),
						'success'
					);

					return response != null;
				})
			);
	}

	resetPass(
		UserId: string,
		Token: string,
		Password: string,
		ConfirmPassword: string
	): Observable<boolean> {
		/* Token = Token.replace(/[+]/g, ' ');
		UserId = UserId.replace(/[+]/g, ' '); */
		return this.http
			.post<any>(`${Constants.API}/Profile/ResetPassword`, {
				UserId,
				Token,
				Password,
				ConfirmPassword
			})
			.pipe(map(response => response != null));
	}
}
